// Third Party
import { useSelector } from "react-redux";

// Redux
import { RootState } from "../store/store";

// Helpers
import { Roles } from "../constants";

type Permission =
  | "assign_role"
  | "upload_gl"
  | "invite_users"
  | "allocate_gl"
  | "update_gl"
  | "certify"
  | "de_certify"
  | "approve"
  | "reject"
  | "auto_approve"
  | "create_company"
  | "access_specific_company"
  | "access_all_companies"
  | "add_regional_admin"
  | "read_coa"
  | "manage_coa";

const ROLES_AND_PERMISSIONS = [
  {
    USER: [],
  },
  {
    MAKER: ["certify", "de_certify"],
  },
  {
    CHECKER: ["de_certify", "approve", "reject"],
  },
  {
    GLOBAL_ADMIN: [
      "assign_role",
      "upload_gl",
      "invite_users",
      "allocate_gl",
      "update_gl",
      "certify",
      "de_certify",
      "approve",
      "reject",
      "auto_approve",
      "create_company",
      "access_specific_company",
      "access_all_companies",
      "add_regional_admin",
    ],
  },
  {
    REGIONAL_ADMIN: [
      "assign_role",
      "upload_gl",
      "invite_users",
      "allocate_gl",
      "update_gl",
      "certify",
      "de_certify",
      "approve",
      "reject",
      "auto_approve",
      "access_specific_company",
      "access_all_companies",
    ],
  },
  {
    COA_VIEWER: ["read_coa"],
  },
  {
    COA_MANAGER: ["read_coa", "manage_coa"],
  },
];

const permissions_by_role = {
  DASHBOARD: [
    Roles.SYSTEM_ADMIN,
    Roles.GLOBAL_ADMIN,
    Roles.REGIONAL_ADMIN,
    Roles.CHECKER,
    Roles.MAKER,
  ],
  LIST_OF_REC: [
    Roles.SYSTEM_ADMIN,
    Roles.GLOBAL_ADMIN,
    Roles.REGIONAL_ADMIN,
    Roles.CHECKER,
    Roles.MAKER,
    // Without this, the "DimeRec" sidebar link (gated on
    // checkPermission("LIST_OF_REC")) stays hidden for a user whose only
    // relevant role is RECORDS_VIEWER, even though the route itself now
    // allows them in.
    Roles.RECORDS_VIEWER,
  ],
  INVITE_USER: [Roles.SYSTEM_ADMIN, Roles.GLOBAL_ADMIN, Roles.REGIONAL_ADMIN],
  INVITE_REGIONAL_ADMIN: [Roles.SYSTEM_ADMIN, Roles.GLOBAL_ADMIN],
  UPLOAD_DUMP: [Roles.SYSTEM_ADMIN, Roles.GLOBAL_ADMIN, Roles.REGIONAL_ADMIN],
  CREATE_COMPANY: [Roles.SYSTEM_ADMIN, Roles.GLOBAL_ADMIN],
  ACCESS_COMPANIES: [
    Roles.SYSTEM_ADMIN,
    Roles.GLOBAL_ADMIN,
    Roles.REGIONAL_ADMIN,
  ],
  // Was [Roles.CHECKER] only — that silently hid the Prepared/Not-Prepared
  // donut from Maker and Global Admin (and Regional Admin/System Admin),
  // who should see it same as Allocated/Not-Allocated. CHECKER_STATUS_RATIO
  // below (Approved/Not-Approved/Rejected) correctly stays Checker-only.
  MAKER_STATUS_RATIO: [
    Roles.SYSTEM_ADMIN,
    Roles.GLOBAL_ADMIN,
    Roles.REGIONAL_ADMIN,
    Roles.MAKER,
    Roles.CHECKER,
  ],
  CHECKER_STATUS_RATIO: [Roles.CHECKER],
  EXPLAINED_STATUS_RATIO: [Roles.CHECKER, Roles.MAKER],
  ALLOCATED_NOT_ALLOCATED_STATUS_RATIO: [
    Roles.SYSTEM_ADMIN,
    Roles.GLOBAL_ADMIN,
    Roles.REGIONAL_ADMIN,
    Roles.MAKER,
    Roles.CHECKER,
  ],
  REPORT_STATUS_TABLE: [Roles.SYSTEM_ADMIN, Roles.MAKER, Roles.CHECKER],
  ADMIN_REPORT_TABLE: [
    Roles.SYSTEM_ADMIN,
    Roles.GLOBAL_ADMIN,
    Roles.REGIONAL_ADMIN,
  ],
};

/**
 * This is the global permission management hook.
 * @returns `boolean` checkPermission
 * @returns `boolean` checkPermissionWithRole
 * @usage checkPermission(PERMISSION_NAME)
 * */
export const useAccessControl = () => {
  const { user, isUserDataLoaded } = useSelector(
    (state: RootState) => state.user,
  );

  const IS_SYSTEM_ADMIN = Boolean(
    user?.roles.find((r) => r.name === Roles.SYSTEM_ADMIN),
  );
  const checkPermission = (
    permissionName: keyof typeof permissions_by_role,
  ) => {
    return Boolean(
      user?.roles.find((r) =>
        permissions_by_role[permissionName].includes(r.name),
      ),
    );
  };

  const checkSpecificModulePermission = (moduleName: Permission) => {
    return user?.permissions.includes(moduleName);
  };

  return {
    checkPermission,
    checkSpecificModulePermission,
    IS_GLOBAL_ADMIN: Boolean(
      user?.roles.find((r) => r.name === Roles.GLOBAL_ADMIN),
    ),
    IS_REGIONAL_ADMIN: Boolean(
      user?.roles.find((r) => r.name === Roles.REGIONAL_ADMIN),
    ),
    IS_CHECKER: Boolean(user?.roles.find((r) => r.name === Roles.CHECKER)),
    IS_MAKER: Boolean(user?.roles.find((r) => r.name === Roles.MAKER)),
    IS_COA_VIEWER: Boolean(
      user?.roles.find((r) => r.name === Roles.COA_VIEWER),
    ),
    IS_COA_MANAGER: Boolean(
      user?.roles.find((r) => r.name === Roles.COA_MANAGER),
    ),
    // View-only DimeRec/DimeXplain access — see Roles.RECORDS_VIEWER.
    IS_RECORDS_VIEWER: Boolean(
      user?.roles.find((r) => r.name === Roles.RECORDS_VIEWER),
    ),
    IS_SYSTEM_ADMIN,
    // True once getUserDetailsThunk (see App.tsx / usersSlice.ts) has
    // resolved -- use this to avoid making role-gated decisions off the
    // default/stale `user` state while that request is still in flight.
    IS_USER_DATA_LOADED: Boolean(isUserDataLoaded),
  };
};
